import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { NotificationsMenu } from "@/components/layout/NotificationsMenu";
import { ProfileMenu } from "@/components/layout/ProfileMenu";
import { MobileMenu } from "@/components/layout/MobileMenu";
import { GuestActions } from "@/components/layout/GuestActions";
import { useGuest } from "@/context/GuestContext";
import { useCreatorOn } from "@/lib/platform";

export const NAV_LINKS = [
  { label: "Watch", to: "/", end: true },
  { label: "Create", to: "/create", gated: "create" },
  { label: "Originals", to: "/originals" },
  { label: "Live Events", to: "/live-events" },
  { label: "My Library", to: "/library", gated: "library" },
  { label: "Pricing", to: "/pricing" },
];

const SearchPill = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const onSearch = location.pathname === "/search";
  const [value, setValue] = useState(onSearch ? params.get("q") || "" : "");
  useEffect(() => { if (!onSearch) setValue(""); }, [onSearch]);
  const change = (v) => {
    setValue(v);
    navigate(`/search?q=${encodeURIComponent(v)}`, { replace: onSearch });
  };
  return (
    <label className="hidden lg:flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#050505]/70 px-4 py-2 w-[230px] xl:w-[300px] focus-within:border-[#FFD86A] focus-within:shadow-[0_0_14px_rgba(212,175,55,0.2)] transition-[border-color,box-shadow]">
      <Search className="h-4 w-4 text-[#D4AF37]" />
      <input
        data-testid="header-search-input"
        value={value}
        onChange={(e) => change(e.target.value)}
        placeholder="Search movies, series, cast..."
        className="bg-transparent outline-none text-sm text-white placeholder:text-[#8B8B8B] w-full"
      />
    </label>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { isGuest, openAuth } = useGuest();
  const creatorOn = useCreatorOn();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${scrolled ? "bg-[#050505]/88 backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.8)]" : "bg-gradient-to-b from-[#050505]/90 via-[#050505]/40 to-transparent"}`}
    >
      <div className="mx-auto flex h-16 md:h-[84px] max-w-[1680px] items-center gap-2.5 sm:gap-4 px-3 sm:px-6 lg:px-10">
        <MobileMenu />
        <Logo />
        <nav className="hidden lg:flex ml-4 xl:ml-6 items-center gap-0.5 rounded-full glass px-1.5 py-1.5" data-testid="desktop-nav">
          {NAV_LINKS.filter((l) => creatorOn || l.gated !== "create").map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={(e) => { if (isGuest && l.gated) { e.preventDefault(); openAuth({ reason: l.gated }); } }}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative rounded-full px-3 xl:px-4 py-1.5 text-sm font-semibold transition-colors duration-300 ${isActive ? "text-[#050505] bg-gradient-to-r from-[#FFE9A3] via-[#F5C451] to-[#D4AF37] shadow-[0_0_16px_rgba(212,175,55,0.35)]" : "text-white/75 hover:text-[#FFD86A]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-3">
          <SearchPill />
          <button
            data-testid="header-search-icon-btn"
            onClick={() => navigate("/search")}
            className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 active:scale-95 transition-[background-color,transform]"
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </button>
          {isGuest ? <GuestActions /> : <><NotificationsMenu /><ProfileMenu /></>}
        </div>
      </div>
      <div className={`gold-hairline transition-opacity duration-500 ${scrolled ? "opacity-100" : "opacity-0"}`} />
    </header>
  );
};
